import { InputGroup } from '@/components/ui/input-group';
import useDebounce from '@/hooks/useDebounce';
import type { InputProps } from '@chakra-ui/react';
import { IconButton, Input } from '@chakra-ui/react';

import { useEffect, useRef, useState } from 'react';
import { Search, Xmark } from 'iconoir-react';

export interface SearchInputProps extends Omit<InputProps, 'value' | 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  debounceTime?: number;
  /** Offer a one-click way back to the unfiltered list once something is typed. */
  clearLabel?: string;
  /** `dark` for the dashboard's dark menu (the Inbox panel); `light` everywhere else. */
  tone?: 'light' | 'dark';
}

// Still the outline input in both tones, ringed by an outline on focus, never a
// shadow. Light: the toolbar's cream field, turning white as it takes focus.
// Dark: a raised block of the menu, like the brand switcher.
const FIELD_TONES = {
  light: {
    icon: 'var(--chakra-colors-ink-muted)',
    input: {
      bg: 'background',
      borderColor: 'hairline.field',
      _hover: { borderColor: 'hairline.control' },
      _focus: {
        bg: 'white',
        borderColor: 'primary.900',
        boxShadow: 'none',
        outlineWidth: '3px',
        outlineColor: 'blackAlpha.200',
        outlineOffset: '0',
      },
    },
  },
  dark: {
    icon: 'var(--chakra-colors-nav-dark-text-subtle)',
    input: {
      bg: 'navDark.surface',
      borderColor: 'navDark.surfaceBorder',
      color: 'navDark.textStrong',
      _placeholder: { color: 'navDark.textSubtle' },
      _hover: { borderColor: 'navDark.surfaceHoverBorder' },
      _focus: {
        borderColor: 'navDark.focusBorder',
        boxShadow: 'none',
        outlineWidth: '3px',
        outlineColor: 'navDark.activeBorder',
        outlineOffset: '0',
      },
    },
  },
} as const;

export const SearchInput: React.FC<SearchInputProps> = ({
  value,
  onChange,
  debounceTime = 300,
  placeholder = 'Search...',
  clearLabel,
  tone = 'light',
  ...props
}) => {
  const [searchQuery, setSearchQuery] = useState(value || '');
  const debouncedSearch = useDebounce(searchQuery, debounceTime);
  const onChangeRef = useRef(onChange);
  const lastEmittedValueRef = useRef(value || '');
  const isUserTypingRef = useRef(false);

  // Keep the onChange ref current
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Sync internal state with external value prop (only when external value changes)
  useEffect(() => {
    const normalizedValue = value || '';
    // Only sync if user is not actively typing and the value actually changed
    if (!isUserTypingRef.current && searchQuery !== normalizedValue) {
      setSearchQuery(normalizedValue);
    }
  }, [value]); // Removed searchQuery from dependency array

  // Handle debounced search changes
  useEffect(() => {
    const trimmed = debouncedSearch.trim();
    const normalizedValue = value || '';

    // Only call onChange if:
    // 1. The trimmed value is different from the current value
    // 2. We haven't already emitted this exact value
    if (trimmed !== normalizedValue && trimmed !== lastEmittedValueRef.current) {
      lastEmittedValueRef.current = trimmed;
      onChangeRef.current(trimmed);
    }

    // Reset typing flag after debounce
    isUserTypingRef.current = false;
  }, [debouncedSearch, value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    isUserTypingRef.current = true; // Mark that user is actively typing
    setSearchQuery(e.target.value);
  };

  return (
    <InputGroup
      w="full"
      gapX={2}
      startOffset="2px"
      startElement={<Search width={16} height={16} color={FIELD_TONES[tone].icon} aria-hidden />}
      endElement={
        clearLabel && searchQuery ? (
          <IconButton
            type="button"
            size="2xs"
            variant="ghost"
            aria-label={clearLabel}
            onClick={() => {
              isUserTypingRef.current = true;
              setSearchQuery('');
            }}
          >
            <Xmark width={14} height={14} aria-hidden />
          </IconButton>
        ) : undefined
      }
      {...props}
    >
      <Input
        variant="outline"
        placeholder={placeholder}
        value={searchQuery}
        onChange={handleInputChange}
        rounded="10px"
        {...FIELD_TONES[tone].input}
      />
    </InputGroup>
  );
};
